import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PaperControl } from '@/module/Author/Paper/PaperControl';
import { ReviewMembershipSelectControl } from '@/module/Nomen/ReviewMembership/ReviewMembershipSelectControl';
import { ReviewTypeSelectControl } from '@/module/Nomen/ReviewType/ReviewTypeSelectControl';

import { AuthorListControl, Content, Form, FormSubmit } from '@/core/components';

import { ReviewSchema } from './model/ReviewSchema';

interface ReviewFormProps extends FormSubmit<ReviewSchema> {
  title: string;
  showWosFields: boolean;
  showScopusFields: boolean;
}

export function ReviewForm(props: ReviewFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit, showWosFields, showScopusFields } =
    props;
  const { t } = useTranslation();
  const form = useForm<ReviewSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            {showWosFields && (
              <ReviewTypeSelectControl<ReviewSchema>
                label={t('reviews.form.fields.reviewType')}
                name="type"
                isSubmitting={isSubmitting}
              />
            )}
            <PaperControl
              isSubmitting={isSubmitting}
              showScopusFields={showScopusFields}
              showWosFields={showWosFields}
            />
          </Content>
          <Content type="full">
            <AuthorListControl<ReviewSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <ReviewMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
